/**
 * Putting the operator's choice onto the running Twilio device.
 *
 * React-free and typed against the smallest shape that does the job rather than
 * against `AudioHelper` itself, so a test can hand these functions a plain
 * object and assert what was called. The SDK's own class satisfies it
 * structurally, which is the only compatibility that matters here.
 *
 * Two halves, applied at DIFFERENT moments, and that split is the correction to
 * the original plan. Choosing an output device costs nothing: no permission
 * prompt, no capture, no red dot in the tab, so it can be set the moment a
 * device is built. `setInputDevice` is the opposite - it opens the microphone
 * and HOLDS it (Twilio's own documentation says so), so doing it at
 * construction would put a permanent recording indicator on the tab of every
 * operator whose browser registers for incoming calls, which is all of them.
 * The input therefore goes on immediately before a call and comes off after it,
 * exactly as the SDK recommends.
 */
import type { AudioChoice } from "./choice";
import type { AudioProcessorHost } from "./gain";

/** One of `speakerDevices` / `ringtoneDevices`. */
export interface AudioSinkCollection {
  set(deviceIdOrIds: string | string[]): Promise<void>;
}

/**
 * The part of Twilio's `AudioHelper` this app touches.
 *
 * The processor half is inherited rather than restated, because only one file
 * uses it and that file owns the explanation: `gain.ts` is where the microphone
 * level is fitted, and everything true about `addProcessor` is true there.
 */
export interface CallAudio extends AudioProcessorHost {
  /** False when the browser has no `setSinkId`, and then nothing below applies. */
  isOutputSelectionSupported: boolean;
  speakerDevices: AudioSinkCollection;
  setInputDevice(deviceId: string): Promise<void>;
  unsetInputDevice(): Promise<void>;
  /** Reads or sets whether the SDK plays its own incoming ringtone. */
  incoming(enable?: boolean): boolean;
  /**
   * Reads or sets whether the SDK plays its own OUTGOING ring - the sound it
   * makes while a call this browser placed is ringing at the other end.
   *
   * The counterpart of `incoming` above and switched off under a condition
   * rather than always: we only have something to put in its place when the
   * operator has uploaded one. See `applySdkRingSounds`.
   */
  outgoing(enable?: boolean): boolean;
  /**
   * The microphone's level, 0 to 1, while a call is up. The SDK only polls it
   * while somebody is listening, so subscribing is what starts the meter - see
   * `useInputLevel`.
   */
  on(event: "inputVolume", listener: (volume: number) => void): unknown;
  removeListener(event: "inputVolume", listener: (volume: number) => void): unknown;
}

/**
 * The id that means "follow the system", which is a real device to Twilio.
 *
 * `set()` refuses an empty list, so clearing a choice cannot be expressed by
 * passing nothing - it is expressed by naming the browser's own default entry.
 */
const SYSTEM_DEFAULT = "default";

/**
 * Stop the SDK ringing over the top of ours.
 *
 * THIS IS NOT A TIDY-UP. The Voice SDK plays its own incoming ringtone, an mp3
 * fetched from `sdk.twilio.com`, and `incoming/useRingtone.ts` synthesises a
 * second one - so until this line existed, a call to the office rang twice, in
 * two different sounds, out of two different output paths. Choosing an output
 * device for one of them would have left the other one still coming out of the
 * laptop, which is the exact complaint this feature was built to answer.
 *
 * Ours is the one kept, for the reason its own file gives: no asset, no CDN, no
 * cache-busting story. That also makes `ringtoneDevices` dead here - it routes
 * only the sound switched off on this line - and the ring's own output is
 * chosen in `sink.ts` instead.
 */
export function silenceSdkRing(audio: CallAudio | null): void {
  audio?.incoming(false);
}

/**
 * Both of the SDK's own rings, decided together.
 *
 * The incoming one is UNCONDITIONAL, because we always have a ring to put in
 * its place - the shipped trill when nothing was uploaded. The outgoing one is
 * CONDITIONAL, because we do not: nothing is shipped for it, so switching the
 * SDK's off with no file of our own would turn every dial into a silent wait
 * and would read as the telephone being broken.
 *
 * One function rather than two calls at the call site, because the two halves
 * of the outgoing decision - silence theirs, play ours - live in two different
 * files and a drift between them is either a doubled ring or no ring at all.
 * `hasOwnOutgoing` is the same question `useRingback` asks before it plays.
 *
 * Reading it back is deliberate: the SDK's own `outgoing()` is a getter as well
 * as a setter, and a device that refused the change is worth not lying about.
 */
export function applySdkRingSounds(audio: CallAudio | null, hasOwnOutgoing: boolean): void {
  if (!audio) return;
  silenceSdkRing(audio);
  try {
    audio.outgoing(!hasOwnOutgoing);
  } catch {
    // An SDK build without the toggle. The operator hears Twilio's sound over
    // their own, which is worse than the intent and better than a device that
    // failed to finish being built.
  }
}

async function setSink(sink: AudioSinkCollection, deviceId: string): Promise<void> {
  try {
    await sink.set(deviceId || SYSTEM_DEFAULT);
  } catch {
    // Twilio rejects an id it has not enumerated, which happens for one tick
    // after a device is built and for good after a headset is unplugged. The
    // collection falls back to the default device on its own in both cases, and
    // the caller re-applies on the next `deviceChange`.
  }
}

/** Where call audio comes out. Safe to call as often as it is worth calling. */
export async function applyAudioOutputs(audio: CallAudio | null, choice: AudioChoice): Promise<void> {
  if (!audio || !audio.isOutputSelectionSupported) return;
  await setSink(audio.speakerDevices, choice.output);
}

/**
 * Which microphone the next call speaks into.
 *
 * Called before `device.connect()` and before `call.accept()`, and again if the
 * operator changes it while a call is up - that last one is the whole point,
 * because "I cannot hear you" arrives mid-conversation and not before it.
 */
export async function applyAudioInput(audio: CallAudio | null, choice: AudioChoice): Promise<void> {
  if (!audio) return;
  try {
    if (choice.input) await audio.setInputDevice(choice.input);
    else await audio.unsetInputDevice();
  } catch {
    // A device that disappeared between the picker and the dial. The call goes
    // ahead on the system default rather than not going ahead at all.
  }
}

/**
 * The call is over: let the microphone go.
 *
 * Without this the tab keeps its recording indicator lit between calls for
 * anybody who has chosen a microphone, which reads as "this CRM is listening"
 * and is the sort of thing an operator only has to see once.
 */
export async function releaseAudioInput(audio: CallAudio | null): Promise<void> {
  try {
    await audio?.unsetInputDevice();
  } catch {
    // Already gone, or never set.
  }
}
